import { LOCALE_TAGS, localizedPath, type LocaleId } from '@bannermatic/core';

/**
 * Everything search engines and link previews read from <head> of a public page. The same
 * description is written into the pre-rendered HTML at build time and applied to the live
 * document when the visitor navigates inside the app.
 */
export interface PageHead {
  title: string;
  description: string;
  /** Schema.org objects placed into one JSON-LD @graph. */
  jsonLd?: Array<Record<string, unknown>>;
  /** Social preview picture (site-relative path); the default Open Graph image otherwise. */
  image?: { path: string; width: number; height: number; alt?: string };
  type?: 'website' | 'article';
}

export interface HeadContext {
  /** Unprefixed page path, e.g. "/" or "/privacy". */
  path: string;
  locale: LocaleId;
  /** Languages the page exists in (for hreflang). */
  locales: readonly LocaleId[];
  /** Absolute origin without a trailing slash. */
  site: string;
}

/**
 * Placeholder for the site origin in pre-rendered HTML: the build doesn't know the domain, the
 * server replaces it with APP_URL when it serves the page.
 */
export const SITE_TOKEN = '%BM_SITE_URL%';

export const DEFAULT_IMAGE = {
  path: '/og-image.png',
  width: 1200,
  height: 630,
  alt: 'Bannermatic — automated banner generation via API & spreadsheets',
};

interface HeadTag {
  tag: 'meta' | 'link' | 'script';
  attrs: Record<string, string>;
  content?: string;
}

/** Open Graph wants "uk_UA", not "uk-UA". */
const ogLocale = (locale: LocaleId) => LOCALE_TAGS[locale].replace('-', '_');

export function canonicalUrl(ctx: Pick<HeadContext, 'site' | 'locale' | 'path'>): string {
  return ctx.site + localizedPath(ctx.locale, ctx.path);
}

function headTags(head: PageHead, ctx: HeadContext): HeadTag[] {
  const url = canonicalUrl(ctx);
  const image = head.image ?? DEFAULT_IMAGE;
  const imageUrl = ctx.site + image.path;
  const meta = (key: 'name' | 'property', id: string, content: string): HeadTag => ({
    tag: 'meta',
    attrs: { [key]: id, content },
  });
  const tags: HeadTag[] = [
    meta('name', 'description', head.description),
    { tag: 'link', attrs: { rel: 'canonical', href: url } },
  ];
  if (ctx.locales.length > 1) {
    for (const l of ctx.locales) {
      tags.push({
        tag: 'link',
        attrs: { rel: 'alternate', hreflang: l, href: ctx.site + localizedPath(l, ctx.path) },
      });
    }
    const fallback = ctx.locales.includes('en') ? 'en' : ctx.locales[0];
    tags.push({
      tag: 'link',
      attrs: {
        rel: 'alternate',
        hreflang: 'x-default',
        href: ctx.site + localizedPath(fallback, ctx.path),
      },
    });
  }
  tags.push(
    meta('property', 'og:type', head.type ?? 'website'),
    meta('property', 'og:site_name', 'Bannermatic'),
    meta('property', 'og:url', url),
    meta('property', 'og:title', head.title),
    meta('property', 'og:description', head.description),
    meta('property', 'og:locale', ogLocale(ctx.locale)),
    ...ctx.locales
      .filter((l) => l !== ctx.locale)
      .map((l) => meta('property', 'og:locale:alternate', ogLocale(l))),
    meta('property', 'og:image', imageUrl),
    meta('property', 'og:image:width', String(image.width)),
    meta('property', 'og:image:height', String(image.height)),
    meta('property', 'og:image:alt', image.alt ?? head.title),
    meta('name', 'twitter:card', 'summary_large_image'),
    meta('name', 'twitter:title', head.title),
    meta('name', 'twitter:description', head.description),
    meta('name', 'twitter:image', imageUrl),
  );
  if (head.jsonLd?.length) {
    tags.push({
      tag: 'script',
      attrs: { type: 'application/ld+json' },
      content: JSON.stringify({ '@context': 'https://schema.org', '@graph': head.jsonLd }),
    });
  }
  return tags;
}

const escapeAttr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
/** JSON inside <script> must not close the tag. */
const escapeScript = (s: string) => s.replace(/</g, '\\u003c');

/** Head markup for the pre-rendered HTML file. */
export function headHtml(head: PageHead, ctx: HeadContext): string {
  const lines = [`<title>${escapeText(head.title)}</title>`];
  for (const t of headTags(head, ctx)) {
    const attrs = Object.entries(t.attrs)
      .map(([k, v]) => ` ${k}="${escapeAttr(v)}"`)
      .join('');
    lines.push(
      t.tag === 'script'
        ? `<script data-bm-head${attrs}>${escapeScript(t.content ?? '')}</script>`
        : `<${t.tag} data-bm-head${attrs} />`,
    );
  }
  return lines.join('\n    ');
}

/** Replaces the page's head tags in the live document (navigation inside the app). */
export function applyHead(head: PageHead, ctx: HeadContext) {
  document.title = head.title;
  document.head.querySelectorAll('[data-bm-head]').forEach((el) => el.remove());
  for (const t of headTags(head, ctx)) {
    const el = document.createElement(t.tag);
    el.setAttribute('data-bm-head', '');
    for (const [k, v] of Object.entries(t.attrs)) el.setAttribute(k, v);
    if (t.content) el.textContent = t.content;
    document.head.appendChild(el);
  }
}
